import React, { useMemo } from 'react';
import { CanvasNode, CanvasEdge } from '../../../hooks/workflows/useWorkflowOperations';
import { CANVAS_CONSTANTS, EDGE_STYLES, EDGE_LABELS } from '../../../constants/workflows';

interface WorkflowEdgesProps {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  startNode: CanvasNode;
  viewportWidth?: number;
}

interface EdgeStyle {
  color: string;
  label: string;
  labelColor: string;
  bgColor: string;
}

const getEdgeStyle = (condition: any): EdgeStyle => {
  if (!condition || !condition.type) {
    return { 
      color: EDGE_STYLES.DEFAULT.color, 
      label: '', 
      labelColor: EDGE_STYLES.DEFAULT.labelColor, 
      bgColor: EDGE_STYLES.DEFAULT.bgColor 
    };
  }
  
  const styleMap: Record<string, { style: { color: string; labelColor: string; bgColor: string }; label: string }> = {
    success: { style: EDGE_STYLES.SUCCESS, label: EDGE_LABELS.success },
    failure: { style: EDGE_STYLES.FAILURE, label: EDGE_LABELS.failure },
    email_reply_received: { style: EDGE_STYLES.EMAIL_REPLY_RECEIVED, label: EDGE_LABELS.email_reply_received },
    email_reply_timeout: { style: EDGE_STYLES.EMAIL_REPLY_TIMEOUT, label: EDGE_LABELS.email_reply_timeout },
    data_match: { style: EDGE_STYLES.DATA_MATCH, label: EDGE_LABELS.data_match },
    classification_match: { style: EDGE_STYLES.CLASSIFICATION_MATCH, label: EDGE_LABELS.classification_match },
    confidence_threshold: { style: EDGE_STYLES.CONFIDENCE_THRESHOLD, label: EDGE_LABELS.confidence_threshold },
    always: { style: EDGE_STYLES.ALWAYS, label: EDGE_LABELS.always }
  };
  
  const config = styleMap[condition.type];
  if (config) {
    return {
      color: config.style.color,
      label: config.label,
      labelColor: config.style.labelColor,
      bgColor: config.style.bgColor
    };
  }
  
  return { 
    color: EDGE_STYLES.DEFAULT.color, 
    label: '', 
    labelColor: EDGE_STYLES.DEFAULT.labelColor, 
    bgColor: EDGE_STYLES.DEFAULT.bgColor 
  };
};

const EdgeComponent: React.FC<{
  edge: CanvasEdge;
  sourceNode: CanvasNode;
  targetNode: CanvasNode;
  edges: CanvasEdge[];
  viewportWidth: number;
  renderMode?: 'pathOnly' | 'labelOnly';
}> = ({ edge, sourceNode, targetNode, edges, viewportWidth, renderMode }) => {
  const nodeWidth = viewportWidth * CANVAS_CONSTANTS.NODE_WIDTH_VW;
  const nodeHeight = CANVAS_CONSTANTS.NODE_HEIGHT;
  
  const sourceOutgoingEdges = edges.filter(e => e.source === edge.source);
  const totalOutgoing = sourceOutgoingEdges.length;
  
  let sourceX: number, sourceY: number;
  
  // Special handling for start node
  if (sourceNode.id === CANVAS_CONSTANTS.START_NODE_ID) {
    sourceX = sourceNode.position.x + (nodeWidth / 2);
    sourceY = sourceNode.position.y + CANVAS_CONSTANTS.NODE_START_HEIGHT;
  } else {
    sourceX = sourceNode.position.x + (nodeWidth / 2);
    sourceY = sourceNode.position.y + nodeHeight;
  }
  
  const targetX = targetNode.position.x + (nodeWidth / 2);
  const targetY = targetNode.position.y;
  
  const edgeStyle = getEdgeStyle(edge.condition);
  
  let pathElement: React.JSX.Element;
  let labelX: number, labelY: number;
  
  if (totalOutgoing > 1) {
    // Y-shaped branching approach
    const stemLength = CANVAS_CONSTANTS.EDGE_STEM_LENGTH;
    const branchY = sourceY + stemLength;
    const branchX = targetX;
    
    const pathData = `
      M ${sourceX} ${sourceY}
      L ${sourceX} ${branchY}
      L ${branchX} ${branchY}
      L ${branchX} ${targetY}
    `;
    
    pathElement = (
      <path
        d={pathData}
        stroke={edgeStyle.color}
        strokeWidth={CANVAS_CONSTANTS.EDGE_STROKE_WIDTH}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
    
    labelX = (sourceX + branchX) / 2;
    labelY = branchY - 15;
  } else {
    // Single edge - straight line
    pathElement = (
      <line
        x1={sourceX}
        y1={sourceY}
        x2={targetX}
        y2={targetY}
        stroke={edgeStyle.color}
        strokeWidth={CANVAS_CONSTANTS.EDGE_STROKE_WIDTH}
        strokeLinecap="round"
      />
    );
    
    labelX = (sourceX + targetX) / 2;
    labelY = (sourceY + targetY) / 2;
  }
  
  if (renderMode === 'pathOnly') {
    return (
      <g>
        {/* Path/Line */}
        {pathElement}
      </g>
    );
  }
  
  if (renderMode === 'labelOnly') {
    return (
      <g>
        {/* Condition label */}
        {edgeStyle.label && (
          <g>
            <rect
              x={labelX - (edgeStyle.label.length * (CANVAS_CONSTANTS.EDGE_LABEL_CHAR_WIDTH / 2)) - CANVAS_CONSTANTS.EDGE_LABEL_PADDING}
              y={labelY - (CANVAS_CONSTANTS.EDGE_LABEL_HEIGHT / 2)}
              width={(edgeStyle.label.length * CANVAS_CONSTANTS.EDGE_LABEL_CHAR_WIDTH) + (CANVAS_CONSTANTS.EDGE_LABEL_PADDING * 2)}
              height={CANVAS_CONSTANTS.EDGE_LABEL_HEIGHT}
              fill={edgeStyle.bgColor}
              stroke={edgeStyle.color}
              strokeWidth="1"
              rx={CANVAS_CONSTANTS.EDGE_LABEL_BORDER_RADIUS}
              ry={CANVAS_CONSTANTS.EDGE_LABEL_BORDER_RADIUS}
            />
            <text
              x={labelX}
              y={labelY + 3}
              fill={edgeStyle.labelColor}
              fontSize={CANVAS_CONSTANTS.EDGE_LABEL_FONT_SIZE}
              textAnchor="middle"
              className="font-semibold select-none"
            >
              {edgeStyle.label}
            </text>
          </g>
        )}
      </g>
    );
  }
  
  return (
    <g>
      {/* Path/Line */}
      {pathElement}
      
      {/* Condition label */}
      {edgeStyle.label && (
        <g>
          <rect
            x={labelX - (edgeStyle.label.length * 3.5)}
            y={labelY - 10}
            width={edgeStyle.label.length * 7}
            height="20"
            fill={edgeStyle.bgColor}
            stroke={edgeStyle.color}
            strokeWidth="1"
            rx="10"
            ry="10"
          />
          <text
            x={labelX}
            y={labelY + 3}
            fill={edgeStyle.labelColor}
            fontSize="10"
            fontWeight="600"
            textAnchor="middle"
            className="font-semibold select-none"
          >
            {edgeStyle.label}
          </text>
        </g>
      )}
    </g>
  );
};

export const WorkflowEdges: React.FC<WorkflowEdgesProps> = ({ 
  nodes, 
  edges, 
  startNode,
  viewportWidth = CANVAS_CONSTANTS.DEFAULT_VIEWPORT_WIDTH
}) => {
  const nodeMap = useMemo(() => {
    const map = new Map();
    const allNodes = [startNode, ...nodes];
    allNodes.forEach(node => map.set(node.id, node));
    return map;
  }, [nodes, startNode]);
  
  // Pre-compute valid edges
  const validEdges = useMemo(() => {
    return edges.map(edge => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);
      return sourceNode && targetNode ? { edge, sourceNode, targetNode } : null;
    }).filter((item): item is { edge: CanvasEdge; sourceNode: CanvasNode; targetNode: CanvasNode } => item !== null);
  }, [edges, nodeMap]);
  
  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 1, overflow: 'visible' }}
    >
      {/* Render paths first */}
      {validEdges.map(({ edge, sourceNode, targetNode }) => (
        <EdgeComponent
          key={`path-${edge.id}`}
          edge={edge}
          sourceNode={sourceNode}
          targetNode={targetNode}
          edges={edges}
          viewportWidth={viewportWidth}
          renderMode="pathOnly"
        />
      ))}
      
      {/* Render labels on top */}
      {validEdges.map(({ edge, sourceNode, targetNode }) => (
        <EdgeComponent
          key={`label-${edge.id}`}
          edge={edge}
          sourceNode={sourceNode}
          targetNode={targetNode}
          edges={edges}
          viewportWidth={viewportWidth}
          renderMode="labelOnly"
        />
      ))}
    </svg>
  );
};